import _ from 'lodash';
import DepsService from 'services/dependencies';
import print from 'print-js';

const USER_STATUS_CREATED = 'CREATED';

// ui helpers
function validateField(form, field) {
  form[field].$validate();
  form[field].$submitted = true;
  if (!form[field].$viewValue) {
    form[field].$submitted = false;
  }
}

function shouldShowValidation(field) {
  return ((field.$$parentForm.$submitted || field.$submitted) && field.$pristine) || field.$touched;
}

function compare(fieldName, a, b) {
  if (a[fieldName] < b[fieldName]) {
    return -1;
  }

  if (a[fieldName] > b[fieldName]) {
    return 1;
  }

  return 0;
}

function b64toBlob(b64Data, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

function downloadFile(context, deps, url, attachment, fileParams = {}) {
  if (attachment.fileSource) {
    const b64Data = attachment.fileSource.split(',');
    const blob = b64toBlob(b64Data[1], attachment.fileType);
    return window.saveAs(blob, attachment.name);
  }
  return deps.restSrv
    .downloadBlob(context, url, null, {
      jurisdictionId: deps.config.jurisdictionId,
      contentId: attachment.id,
    })
    .promise.then((result) => {
      const blob = new Blob([result.data], fileParams);
      window.saveAs(blob, attachment.name);
    });
}

function printElement(elementId) {
  print({
    printable: elementId,
    type: 'html',
    documentTitle: '',
    ignoreElements: ['action-column'],
  });
}

function redirectToFirstPageFromMenu(deps, stateParams = {}, options = {}) {
  const session = JSON.parse(sessionStorage['ngStorage-userSession']);
  const { sidemenu } = session;

  const firstAllowedPageFromMenu = sidemenu.find((menuElement) => menuElement.hasAccess === true);

  deps.$state.go(firstAllowedPageFromMenu.sref, stateParams, options);
}

function redirectToPasswordReset(deps, stateParams = {}, options = {}) {
  const title = deps.gettextCatalog.getString('Password reset required!');
  const content = deps.gettextCatalog.getString(
    'Your password must be reset, click OK button to be redirected to Create New Password screen.'
  );

  stateParams.skipDirtyForms = true;

  return deps.Notifications.showAlert(title, content).result.then(() => deps.$state.go('auth-changePassword', stateParams, options));
}

function redirectToHome(deps, stateParams = {}, options = {}) {
  deps.$state.go('home-view', stateParams, options);
}

function redirectAfterLogin(deps, model, attemptedRoute, stateParams = { skipDirtyForms: true }, options = {}) {
  const { session } = deps.$ngRedux.getState();
  const { loggedIn = false, privileges } = session;

  if (loggedIn && attemptedRoute) {
    const redirect = _.get(privileges, attemptedRoute.replace(/(-)/g, '.'));
    if (redirect) return deps.$state.go(attemptedRoute, stateParams, options);
  }
  if (loggedIn) {
    return redirectToFirstPageFromMenu(deps, stateParams, options);
  }
  if (attemptedRoute) {
    return deps.$state.go(attemptedRoute, stateParams, options);
  }
  return redirectToHome(deps, stateParams, options);
}

function getLocales() {
  const { config } = DepsService.$ngRedux.getState();
  const { locales } = config;
  return locales;
}

/*
 * CMS helpers
 */
function getFieldsFromEntry(entry) {
  const locales = getLocales();
  const fields = {};

  Object.keys(entry.fields || {}).forEach((key) => {
    if (locales.findIndex((locale) => locale.code === key) > -1) {
      fields[key] = entry.fields[key].reduce((fieldObj, field) => {
        fieldObj[field.id] = field.value;
        return fieldObj;
      }, {});
    }
  });

  return fields;
}

function setFieldsToEntry(entry) {
  const locales = getLocales();
  const fields = {};

  locales.map((locale) => {
    if (entry[locale.code]) {
      fields[locale.code] = Object.keys(entry[locale.code]).reduce(
        (fields, key) =>
          fields.concat([
            {
              id: key,
              value: entry[locale.code][key],
            },
          ]),
        [],
        entry[locale.code]
      );
    }
  });
  return fields;
}

/*
 * END CMS helpers
 */

function dragMove(eventObj) {
  const scrollMargin = document.getElementById('navbar').offsetHeight;
  const below = eventObj.clientY > window.innerHeight - scrollMargin;
  const above = eventObj.clientY < scrollMargin;
  if (below || above) {
    document.getElementById('body').scrollTop += below ? scrollMargin : -scrollMargin;
  }
}

function generateUUID() {
  const uuid = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ ((window.crypto || window.msCrypto).getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
  return uuid;
}

// get last children
function extractDictionaryItem(dictionaryArray, availableStatuses = []) {
  return dictionaryArray.reduce((reduced, item) => {
    const statusObj = item.typeCodeTexts[0];
    const path = _.last(item.path.split('.'));
    if (availableStatuses.length === 0 || availableStatuses.indexOf(path) > -1) {
      reduced[item.typeCode] = {
        path: path.toUpperCase(),
        displayText: _.get(statusObj, 'displayText') || '',
      };
    }
    return reduced;
  }, {});
}

// get exact childs from the given dictionary path
function extractDictionaryRoot(dictionaryArray, path) {
  return dictionaryArray.reduce((reduced, item) => {
    const statusObj = item.typeCodeTexts[0];
    const splittedLength = item.path.split('.').length;
    const pathSplitLength = path.split('.').length + 1;

    if (pathSplitLength === splittedLength) {
      reduced[item.typeCode] = {
        path: path.toUpperCase(),
        displayText: _.get(statusObj, 'displayText') || '',
      };
    }
    return reduced;
  }, {});
}

function trimHtml(content, trim = true) {
  const div = document.createElement('div');
  div.innerHTML = content;
  return trim ? div.innerText.trim() : div.innerText;
}

function setHeader(extension) {
  switch (extension) {
    case 'csv': {
      return { Accept: 'text/csv' };
    }
    case 'xlsx': {
      return { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
    }
    default:
      return {};
  }
}

function calculateDaysLeftToPercentages(daysLeft, threshold) {
  const shouldCalculatePercentages = threshold >= daysLeft && daysLeft >= 0;
  const isBondRenewalExceeded = daysLeft <= 0;

  if (shouldCalculatePercentages) {
    const percent = ((threshold - daysLeft) / threshold) * 100;
    return Math.round(percent);
  }

  if (isBondRenewalExceeded) return 100;

  return 0;
}

export default {
  extractDictionaryItem,
  extractDictionaryRoot,
  validateField,
  shouldShowValidation,
  compare,
  b64toBlob,
  printElement,
  redirectAfterLogin,
  redirectToPasswordReset,
  redirectToHome,
  redirectToFirstPageFromMenu,
  getFieldsFromEntry,
  setFieldsToEntry,
  dragMove,
  downloadFile,
  generateUUID,
  trimHtml,
  setHeader,
  calculateDaysLeftToPercentages,
};



// WEBPACK FOOTER //
// ./src/modules/app/app-helpers.js